import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/config/api";
import { showConfirm, showError, showSuccess } from "@/utils/dialog";
import { toLocalDateInputValue } from "@/utils/date";
import {
  Users,
  DollarSign,
  FileText,
  Activity,
  Filter,
} from "lucide-react";

export default function SuperAdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [auditLogStats, setAuditLogStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [filters, setFilters] = useState({
    user_id: "",
    action: "",
    entity_type: "",
    start_date: "",
    end_date: "",
  });
  const [packages, setPackages] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [saasAnalytics, setSaasAnalytics] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    company_name: "",
    legal_name: "",
    company_email: "",
    admin_email: "",
    admin_name: "",
    admin_password: "",
    package_id: "",
  });
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [companySubscriptionModal, setCompanySubscriptionModal] = useState(null);
  const [companySubscriptionPackageId, setCompanySubscriptionPackageId] = useState("");
  useEffect(() => {
    if (companySubscriptionModal && packages.length > 0 && !companySubscriptionPackageId) {
      setCompanySubscriptionPackageId(packages[0].id);
    }
  }, [companySubscriptionModal, packages, companySubscriptionPackageId]);
  const [passwordForm, setPasswordForm] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [extendForm, setExtendForm] = useState({
    months: "",
    days: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [
        overviewData,
        userStatsData,
        subscriptionStatsData,
        auditStatsData,
        saasData,
      ] = await Promise.all([
        api.superAdmin.getOverview(),
        api.superAdmin.getUserStats(),
        api.superAdmin.getSubscriptionStats(),
        api.superAdmin.getAuditLogStats(),
        api.superAdmin.getSaasAnalytics().catch(() => null),
      ]);

      setOverview(overviewData);
      setUserStats(userStatsData);
      setSubscriptionStats(subscriptionStatsData);
      setAuditLogStats(auditStatsData);
      setSaasAnalytics(saasData);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = useCallback(async () => {
    try {
      const data = await api.superAdmin.getAuditLogs({
        ...filters,
        limit: 100,
        offset: 0,
      });
      setAuditLogs(data.logs);
    } catch (error) {
      console.error("Failed to load audit logs:", error);
    }
  }, [filters]);

  const loadPackages = async () => {
    try {
      const data = await api.superAdmin.getPackages();
      setPackages(data);
    } catch (error) {
      console.error("Failed to load packages:", error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      await showError("New passwords do not match", "Validation Error");
      return;
    }
    try {
      await api.superAdmin.changePassword(
        passwordForm.old_password,
        passwordForm.new_password
      );
      await showSuccess("Password changed successfully");
      setShowPasswordModal(false);
      setPasswordForm({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      await showError(error.message || "Failed to change password");
    }
  };

  const loadAdmins = async () => {
    try {
      const [usersData, subscriptionsData] = await Promise.all([
        api.superAdmin.getUsers(),
        api.superAdmin.getSubscriptions(),
      ]);
      // Filter only admin users
      const admins = usersData.filter((u) => u.role === "admin");
      setAdminUsers(admins);
      setSubscriptions(subscriptionsData);
    } catch (error) {
      console.error("Failed to load admins:", error);
    }
  };

  const handleDeactivateAdmin = async (userId, isActive) => {
    try {
      await api.superAdmin.updateAdminStatus(userId, !isActive);
      await showSuccess(`Admin ${isActive ? "deactivated" : "activated"} successfully`);
      loadAdmins();
    } catch (error) {
      await showError(error.message || "Failed to update admin status");
    }
  };

  const handleExtendSubscription = async (e) => {
    e.preventDefault();
    if (!selectedSubscription) return;
    if (!extendForm.months && !extendForm.days) {
      await showError("Please enter either months or days", "Validation Error");
      return;
    }
    try {
      await api.superAdmin.extendSubscription(
        selectedSubscription.id,
        extendForm.months ? parseInt(extendForm.months) : null,
        extendForm.days ? parseInt(extendForm.days) : null
      );
      await showSuccess("Subscription validity extended successfully");
      setShowExtendModal(false);
      setSelectedSubscription(null);
      setExtendForm({ months: "", days: "" });
      loadAdmins();
    } catch (error) {
      await showError(error.message || "Failed to extend subscription");
    }
  };

  useEffect(() => {
    if (activeTab === "audit-logs") {
      loadAuditLogs();
    } else if (activeTab === "packages") {
      loadPackages();
    } else if (activeTab === "admins") {
      loadAdmins();
    } else if (activeTab === "companies") {
      api.superAdmin.getCompanies().then(setCompanies).catch(() => setCompanies([]));
    }
  }, [activeTab, filters, loadAuditLogs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-secondary">
            Super Admin Dashboard
          </h1>
          <p className="text-sm text-stone-600 mt-1">
            Platform insights and audit logs
          </p>
        </div>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Change Password
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone-200">
        <nav className="flex space-x-8">
          {[
            { id: "overview", label: "Overview" },
            { id: "saas-analytics", label: "SaaS Analytics" },
            { id: "companies", label: "Companies" },
            { id: "users", label: "Users" },
            { id: "subscriptions", label: "Subscriptions" },
            { id: "packages", label: "Packages" },
            { id: "admins", label: "Admin Management" },
            { id: "revenue", label: "Revenue" },
            { id: "audit-logs", label: "Audit Logs" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-stone-500 hover:text-stone-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && overview && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600 mb-1">Total Users</p>
                  <p className="text-3xl font-semibold text-secondary">
                    {overview.users.total}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    {overview.users.active} active
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users size={24} className="text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600 mb-1">
                    Active Subscriptions
                  </p>
                  <p className="text-3xl font-semibold text-secondary">
                    {overview.subscriptions.active}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    {overview.subscriptions.total} total
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <FileText size={24} className="text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600 mb-1">Total Revenue</p>
                  <p className="text-3xl font-semibold text-secondary">
                    ${overview.revenue.total.toFixed(2)}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    ${overview.revenue.monthly.toFixed(2)} this month
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <DollarSign size={24} className="text-amber-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600 mb-1">
                    Platform Activity
                  </p>
                  <p className="text-3xl font-semibold text-secondary">
                    {overview.platform_activity.total_invoices}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    {overview.platform_activity.total_customers} customers
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Activity size={24} className="text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SaaS Analytics Tab */}
      {activeTab === "saas-analytics" && saasAnalytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <p className="text-sm text-stone-600 mb-1">Total Companies</p>
              <p className="text-3xl font-semibold text-secondary">{saasAnalytics.total_companies}</p>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <p className="text-sm text-stone-600 mb-1">Active Subscriptions</p>
              <p className="text-3xl font-semibold text-emerald-600">{saasAnalytics.active_subscriptions}</p>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <p className="text-sm text-stone-600 mb-1">MRR (USD)</p>
              <p className="text-3xl font-semibold text-primary">${Number(saasAnalytics.mrr || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <p className="text-sm text-stone-600 mb-1">Churn Rate (30d)</p>
              <p className="text-3xl font-semibold text-amber-600">{Number(saasAnalytics.churn_rate || 0).toFixed(1)}%</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">Upcoming Renewals (30 days)</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-stone-600">
                    <th className="py-2">Company</th>
                    <th className="py-2">Package</th>
                    <th className="py-2">End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(saasAnalytics.upcoming_renewals || []).map((r) => (
                    <tr key={r.id} className="border-b border-stone-100">
                      <td className="py-2">{r.company_name || "—"}</td>
                      <td className="py-2">{r.package_name || "—"}</td>
                      <td className="py-2">{r.end_date ? new Date(r.end_date).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!saasAnalytics.upcoming_renewals || saasAnalytics.upcoming_renewals.length === 0) && (
                <p className="text-stone-500 py-4">No renewals in the next 30 days.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Companies Tab */}
      {activeTab === "companies" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setShowOnboardModal(true);
                if (packages.length === 0) loadPackages();
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Onboard Company
            </button>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-stone-700">Company</th>
                  <th className="text-left py-3 px-4 font-medium text-stone-700">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-stone-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-stone-700">Plan</th>
                  <th className="text-left py-3 px-4 font-medium text-stone-700">Users</th>
                  <th className="text-left py-3 px-4 font-medium text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-4">{c.name}</td>
                    <td className="py-3 px-4">{c.email || "—"}</td>
                    <td className="py-3 px-4">{c.status || "active"}</td>
                    <td className="py-3 px-4">{c.current_package_name || "—"}</td>
                    <td className="py-3 px-4">{c.user_count ?? "—"}</td>
                    <td className="py-3 px-4">
                      <button
                        type="button"
                        onClick={() => {
                          if (packages.length === 0) loadPackages();
                          setCompanySubscriptionModal(c);
                          setCompanySubscriptionPackageId(c.current_package_id || packages[0]?.id || "");
                        }}
                        className="text-primary hover:underline text-sm"
                      >
                        Change subscription
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {companies.length === 0 && (
              <p className="text-stone-500 py-8 text-center">No companies yet. Use Onboard Company to add one.</p>
            )}
          </div>
        </div>
      )}

      {/* Change Company Subscription Modal */}
      {companySubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-stone-900 mb-2">Change subscription</h3>
            <p className="text-sm text-stone-600 mb-4">{companySubscriptionModal.name}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-1">Package</label>
              <select
                value={companySubscriptionPackageId}
                onChange={(e) => setCompanySubscriptionPackageId(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
              >
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — {p.currency} {p.price}/{p.billing_period}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.superAdmin.updateCompanySubscription(companySubscriptionModal.id, { package_id: companySubscriptionPackageId });
                    setCompanySubscriptionModal(null);
                    if (activeTab === "companies") api.superAdmin.getCompanies().then(setCompanies).catch(() => {});
                  } catch (err) {
                    await showError(err.message || "Failed to update subscription");
                  }
                }}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setCompanySubscriptionModal(null)}
                className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboard Company Modal */}
      {showOnboardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-stone-900 mb-4">Onboard Company</h3>
              {packages.length === 0 && (
                <p className="text-amber-600 text-sm mb-2">Loading packages…</p>
              )}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!onboardForm.company_name || !onboardForm.admin_email || !onboardForm.admin_name || !onboardForm.admin_password || !onboardForm.package_id) {
                    await showError("Fill required fields: Company name, Admin email, Admin name, Password, Package", "Validation Error");
                    return;
                  }
                  try {
                    await api.superAdmin.onboardCompany({
                      company_name: onboardForm.company_name,
                      legal_name: onboardForm.legal_name || undefined,
                      company_email: onboardForm.company_email || undefined,
                      admin_email: onboardForm.admin_email,
                      admin_name: onboardForm.admin_name,
                      admin_password: onboardForm.admin_password,
                      package_id: onboardForm.package_id,
                    });
                    setShowOnboardModal(false);
                    setOnboardForm({ company_name: "", legal_name: "", company_email: "", admin_email: "", admin_name: "", admin_password: "", package_id: "" });
                    if (activeTab === "companies") api.superAdmin.getCompanies().then(setCompanies).catch(() => {});
                  } catch (err) {
                    await showError(err.message || "Failed to onboard company");
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Company name *</label>
                  <input
                    type="text"
                    value={onboardForm.company_name}
                    onChange={(e) => setOnboardForm((p) => ({ ...p, company_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Legal name</label>
                  <input
                    type="text"
                    value={onboardForm.legal_name}
                    onChange={(e) => setOnboardForm((p) => ({ ...p, legal_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Company email</label>
                  <input
                    type="email"
                    value={onboardForm.company_email}
                    onChange={(e) => setOnboardForm((p) => ({ ...p, company_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Admin email *</label>
                  <input
                    type="email"
                    value={onboardForm.admin_email}
                    onChange={(e) => setOnboardForm((p) => ({ ...p, admin_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Admin name *</label>
                  <input
                    type="text"
                    value={onboardForm.admin_name}
                    onChange={(e) => setOnboardForm((p) => ({ ...p, admin_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Admin password *</label>
                  <input
                    type="password"
                    value={onboardForm.admin_password}
                    onChange={(e) => setOnboardForm((p) => ({ ...p, admin_password: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Package *</label>
                  <select
                    value={onboardForm.package_id}
                    onChange={(e) => setOnboardForm((p) => ({ ...p, package_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                    required
                  >
                    <option value="">Select package</option>
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>{pkg.name} - ${pkg.price}/{pkg.billing_period}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <button type="button" onClick={() => setShowOnboardModal(false)} className="px-4 py-2 border border-stone-300 rounded-lg text-stone-700">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">Onboard</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === "users" && userStats && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">
              User Statistics
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-stone-600">Total Users</p>
                <p className="text-2xl font-semibold text-secondary">
                  {userStats.total_users}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-600">Active Users</p>
                <p className="text-2xl font-semibold text-emerald-600">
                  {userStats.active_users}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-600">Inactive Users</p>
                <p className="text-2xl font-semibold text-red-600">
                  {userStats.inactive_users}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-600">New This Month</p>
                <p className="text-2xl font-semibold text-blue-600">
                  {userStats.new_users_this_month}
                </p>
              </div>
            </div>
            <div className="mt-6">
              <p className="text-sm font-semibold text-stone-700 mb-2">
                Users by Role
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(userStats.users_by_role).map(
                  ([role, count]) => (
                    <div key={role} className="bg-stone-50 rounded-lg p-3">
                      <p className="text-xs text-stone-600 capitalize">
                        {role}
                      </p>
                      <p className="text-xl font-semibold text-secondary">
                        {count}
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === "subscriptions" && subscriptionStats && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">
              Subscription Statistics
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-sm text-stone-600">Total Subscriptions</p>
                <p className="text-2xl font-semibold text-secondary">
                  {subscriptionStats.total_subscriptions}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-600">Active Subscriptions</p>
                <p className="text-2xl font-semibold text-emerald-600">
                  {subscriptionStats.active_subscriptions}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-600">Total Revenue</p>
                <p className="text-2xl font-semibold text-amber-600">
                  ${subscriptionStats.total_revenue.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-600">Monthly Revenue</p>
                <p className="text-2xl font-semibold text-blue-600">
                  ${subscriptionStats.monthly_revenue.toFixed(2)}
                </p>
              </div>
            </div>
            <div className="mt-6">
              <p className="text-sm font-semibold text-stone-700 mb-2">
                Subscriptions by Status
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {Object.entries(subscriptionStats.subscriptions_by_status).map(
                  ([status, count]) => (
                    <div key={status} className="bg-stone-50 rounded-lg p-3">
                      <p className="text-xs text-stone-600 capitalize">
                        {status}
                      </p>
                      <p className="text-xl font-semibold text-secondary">
                        {count}
                      </p>
                    </div>
                  )
                )}
              </div>
              <p className="text-sm font-semibold text-stone-700 mb-2">
                Subscriptions by Package
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(subscriptionStats.subscriptions_by_package).map(
                  ([packageName, count]) => (
                    <div
                      key={packageName}
                      className="bg-stone-50 rounded-lg p-3"
                    >
                      <p className="text-xs text-stone-600">{packageName}</p>
                      <p className="text-xl font-semibold text-secondary">
                        {count}
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Tab */}
      {activeTab === "revenue" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">
              Revenue Insights
            </h2>
            <p className="text-stone-600">
              Revenue data will be displayed here. Use date filters to view
              specific periods.
            </p>
          </div>
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === "audit-logs" && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Filter size={20} />
              Filter Audit Logs
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  User ID
                </label>
                <input
                  type="text"
                  value={filters.user_id}
                  onChange={(e) =>
                    setFilters({ ...filters, user_id: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  placeholder="Filter by user ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Action
                </label>
                <input
                  type="text"
                  value={filters.action}
                  onChange={(e) =>
                    setFilters({ ...filters, action: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  placeholder="e.g., login, create_invoice"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) =>
                    setFilters({ ...filters, start_date: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) =>
                    setFilters({ ...filters, end_date: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
            </div>
          </div>

          {/* Audit Logs Stats */}
          {auditLogStats && (
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-stone-900 mb-4">
                Audit Log Statistics
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-stone-600">Total Logs</p>
                  <p className="text-2xl font-semibold text-secondary">
                    {auditLogStats.total_logs}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-stone-600">Last 24 Hours</p>
                  <p className="text-2xl font-semibold text-blue-600">
                    {auditLogStats.recent_activity_24h}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-stone-600">Success</p>
                  <p className="text-2xl font-semibold text-emerald-600">
                    {auditLogStats.logs_by_status?.success || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-stone-600">Failures</p>
                  <p className="text-2xl font-semibold text-red-600">
                    {auditLogStats.logs_by_status?.failure || 0}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Audit Logs Table */}
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Entity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-8 text-center text-stone-500"
                      >
                        No audit logs found
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-stone-50">
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div>
                            <p className="font-medium text-stone-900">
                              {log.user_name || "Anonymous"}
                            </p>
                            <p className="text-xs text-stone-500">
                              {log.user_email}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {log.entity_type}{" "}
                          {log.entity_id && `(${log.entity_id.slice(0, 8)}...)`}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === "success"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {log.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {log.ip_address}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Packages Tab */}
      {activeTab === "packages" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-stone-900">
              Subscription Packages
            </h2>
            <button
              onClick={() => {
                setEditingPackage(null);
                setShowPackageModal(true);
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Create Package
            </button>
          </div>

          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Max Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Max Invoices
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {packages.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-8 text-center text-stone-500"
                      >
                        No packages found
                      </td>
                    </tr>
                  ) : (
                    packages.map((pkg) => (
                      <tr key={pkg.id} className="hover:bg-stone-50">
                        <td className="px-6 py-4 text-sm font-medium text-stone-900">
                          {pkg.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {pkg.currency} {pkg.price} / {pkg.billing_period}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {pkg.max_users}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {pkg.max_invoices}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              pkg.is_active
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {pkg.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              setEditingPackage(pkg);
                              setShowPackageModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              if (
                                await showConfirm(
                                  "Are you sure you want to delete this package?",
                                  "Delete Package",
                                  "Delete",
                                )
                              ) {
                                try {
                                  await api.superAdmin.deletePackage(pkg.id);
                                  loadPackages();
                                } catch (error) {
                                  await showError(
                                    error.message || "Failed to delete package"
                                  );
                                }
                              }
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Admin Management Tab */}
      {activeTab === "admins" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-stone-900">
              Admin Management
            </h2>
            <button
              onClick={() => setShowAdminModal(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Create Admin (Offline)
            </button>
          </div>

          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Name / Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Subscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Valid Until
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {adminUsers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-8 text-center text-stone-500"
                      >
                        No admin users found
                      </td>
                    </tr>
                  ) : (
                    adminUsers.map((admin) => {
                      const adminSubscription = subscriptions.find(
                        (s) => s.user_id === admin.id && s.status === "active"
                      );
                      return (
                        <tr key={admin.id} className="hover:bg-stone-50">
                          <td className="px-6 py-4 text-sm">
                            <div>
                              <p className="font-medium text-stone-900">
                                {admin.name}
                              </p>
                              <p className="text-xs text-stone-500">
                                {admin.email}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-stone-600">
                            {admin.company_name || "-"}
                          </td>
                          <td className="px-6 py-4 text-sm text-stone-600">
                            {adminSubscription ? (
                              <div>
                                <p className="font-medium">
                                  {adminSubscription.package_name}
                                </p>
                                <p className="text-xs text-stone-500">
                                  {adminSubscription.currency}{" "}
                                  {adminSubscription.price} /{" "}
                                  {adminSubscription.billing_period}
                                </p>
                              </div>
                            ) : (
                              <span className="text-red-600">
                                No Subscription
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                admin.is_active
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {admin.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-stone-600">
                            {adminSubscription
                              ? new Date(
                                  adminSubscription.end_date
                                ).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              {adminSubscription && (
                                <button
                                  onClick={() => {
                                    setSelectedSubscription(adminSubscription);
                                    setShowExtendModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  Extend
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  handleDeactivateAdmin(
                                    admin.id,
                                    admin.is_active
                                  )
                                }
                                className={`text-sm ${
                                  admin.is_active
                                    ? "text-red-600 hover:text-red-800"
                                    : "text-emerald-600 hover:text-emerald-800"
                                }`}
                              >
                                {admin.is_active ? "Deactivate" : "Activate"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Extend Subscription Modal */}
      {showExtendModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">
              Extend Subscription Validity
            </h2>
            <p className="text-sm text-stone-600 mb-4">
              Package: {selectedSubscription.package_name}
              <br />
              Current End Date:{" "}
              {new Date(selectedSubscription.end_date).toLocaleDateString()}
            </p>
            <form onSubmit={handleExtendSubscription} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Months
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={extendForm.months}
                    onChange={(e) =>
                      setExtendForm({ ...extendForm, months: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={extendForm.days}
                    onChange={(e) =>
                      setExtendForm({ ...extendForm, days: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  Extend
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowExtendModal(false);
                    setSelectedSubscription(null);
                    setExtendForm({ months: "", days: "" });
                  }}
                  className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Old Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.old_password}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        old_password: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        new_password: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        confirm_password: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({
                      old_password: "",
                      new_password: "",
                      confirm_password: "",
                    });
                  }}
                  className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Package Modal */}
      {showPackageModal && (
        <PackageModal
          package={editingPackage}
          onClose={() => {
            setShowPackageModal(false);
            setEditingPackage(null);
          }}
          onSave={async (packageData) => {
            try {
              if (editingPackage) {
                await api.superAdmin.updatePackage(
                  editingPackage.id,
                  packageData
                );
              } else {
                await api.superAdmin.createPackage(packageData);
              }
              loadPackages();
              setShowPackageModal(false);
              setEditingPackage(null);
            } catch (error) {
              await showError(error.message || "Failed to save package");
            }
          }}
        />
      )}

      {/* Admin Creation Modal */}
      {showAdminModal && (
        <AdminModal
          packages={packages}
          onClose={() => setShowAdminModal(false)}
          onSave={async (adminData) => {
            try {
              await api.superAdmin.createAdmin(adminData);
              await showSuccess("Admin created successfully");
              setShowAdminModal(false);
              loadData();
            } catch (error) {
              await showError(error.message || "Failed to create admin");
            }
          }}
        />
      )}
    </div>
  );
}

// Parse package features to array (safe)
function parseFeaturesToArray(features) {
  if (features == null) return [];
  if (Array.isArray(features)) return features.map((f) => (typeof f === "string" ? f : String(f)));
  if (typeof features === "string") {
    try {
      const parsed = JSON.parse(features);
      return Array.isArray(parsed) ? parsed.map((f) => String(f)) : [features];
    } catch {
      return [features];
    }
  }
  return [];
}

// Package Modal Component
function PackageModal({ package: pkg, onClose, onSave }) {
  const [featuresList, setFeaturesList] = useState(() => parseFeaturesToArray(pkg?.features));
  const [formData, setFormData] = useState({
    name: pkg?.name || "",
    description: pkg?.description || "",
    price: pkg?.price || "",
    currency: pkg?.currency || "USD",
    billing_period: pkg?.billing_period || "monthly",
    max_users: pkg?.max_users || 5,
    max_invoices: pkg?.max_invoices || 100,
    is_active: pkg?.is_active !== undefined ? pkg.is_active : true,
    display_order: pkg?.display_order || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const features = featuresList.filter((f) => String(f).trim() !== "");
    onSave({
      ...formData,
      price: parseFloat(formData.price),
      max_users: parseInt(formData.max_users, 10) || 0,
      max_invoices: parseInt(formData.max_invoices, 10) || 0,
      display_order: parseInt(formData.display_order, 10) || 0,
      features,
    });
  };

  const addFeature = () => setFeaturesList((prev) => [...prev, ""]);
  const removeFeature = (index) => setFeaturesList((prev) => prev.filter((_, i) => i !== index));
  const setFeature = (index, value) => setFeaturesList((prev) => prev.map((f, i) => (i === index ? value : f)));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {pkg ? "Edit Package" : "Create Package"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Price *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              >
                <option value="USD">USD</option>
                <option value="OMR">OMR</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Billing Period
              </label>
              <select
                value={formData.billing_period}
                onChange={(e) =>
                  setFormData({ ...formData, billing_period: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Max Users
              </label>
              <input
                type="number"
                value={formData.max_users}
                onChange={(e) =>
                  setFormData({ ...formData, max_users: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Max Invoices
              </label>
              <input
                type="number"
                value={formData.max_invoices}
                onChange={(e) =>
                  setFormData({ ...formData, max_invoices: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Features (one per line)
            </label>
            <div className="space-y-2">
              {featuresList.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => setFeature(index, e.target.value)}
                    placeholder={`Feature ${index + 1}`}
                    className="flex-1 px-4 py-2 border border-stone-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="text-sm text-primary hover:underline"
              >
                + Add feature
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              className="w-4 h-4"
            />
            <label htmlFor="is_active" className="text-sm text-stone-700">
              Active
            </label>
          </div>
          <div className="flex gap-2 mt-6">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              {pkg ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Admin Creation Modal Component
function AdminModal({ packages, onClose, onSave }) {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    company_name: "",
    package_id: packages[0]?.id || "",
    start_date: toLocalDateInputValue(),
    end_date: "",
  });
  const [emailError, setEmailError] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (formData.package_id) {
      const selectedPackage = packages.find((p) => p.id === formData.package_id);
      if (selectedPackage) {
        const endDate = new Date();
        endDate.setMonth(
          endDate.getMonth() +
            (selectedPackage.billing_period === "yearly" ? 12 : 1),
        );
        setFormData((prev) => ({
          ...prev,
          end_date: toLocalDateInputValue(endDate),
        }));
      }
    }
  }, [formData.package_id, packages]);

  const validateEmail = useCallback(async (email) => {
    if (!email || !email.includes("@")) {
      setEmailError("");
      return;
    }

    setCheckingEmail(true);
    setEmailError("");

    try {
      const response = await api.users.checkEmail(email);
      if (response.exists) {
        setEmailError("This email is already registered");
      } else {
        setEmailError("");
      }
    } catch (error) {
      console.error("Email validation error:", error);
      // Don't show error on validation failure, just log it
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  const handleEmailChange = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, email: value });
    setEmailError(""); // Clear previous error on change

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced validation
    if (value && value.includes("@")) {
      debounceTimer.current = setTimeout(() => {
        validateEmail(value);
      }, 500);
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check email one more time before submitting
    if (formData.email) {
      const emailCheck = await api.users.checkEmail(formData.email.trim());
      if (emailCheck.exists) {
        setEmailError("This email is already registered");
        return;
      }
    }

    if (emailError === "This email is already registered") {
      await showError("Please use a unique email address.", "Validation Error");
      return;
    }

    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full">
        <h2 className="text-xl font-semibold mb-4">
          Create Admin (Offline Payment)
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
              onBlur={() => formData.email && validateEmail(formData.email)}
              className={`w-full px-4 py-2 border rounded-lg ${
                emailError
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : "border-stone-300"
              }`}
              required
            />
            {checkingEmail && (
              <p className="text-xs text-stone-500 mt-1">Checking email...</p>
            )}
            {emailError && (
              <p className="text-xs text-red-600 mt-1">{emailError}</p>
            )}
            {!emailError && formData.email && !checkingEmail && (
              <p className="text-xs text-emerald-600 mt-1">
                Email is available
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Password *
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) =>
                setFormData({ ...formData, company_name: e.target.value })
              }
              className="w-full px-4 py-2 border border-stone-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Package *
            </label>
            <select
              value={formData.package_id}
              onChange={(e) =>
                setFormData({ ...formData, package_id: e.target.value })
              }
              className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              required
            >
              <option value="">Select Package</option>
              {packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name} - {pkg.currency} {pkg.price} / {pkg.billing_period}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) =>
                  setFormData({ ...formData, end_date: e.target.value })
                }
                className="w-full px-4 py-2 border border-stone-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                checkingEmail ||
                emailError === "This email is already registered"
              }
            >
              Create Admin
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
